// CategoryDisplayer.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { FaChevronLeft, FaPlus, FaFolder, FaList, FaUnlink, FaChevronUp, FaChevronDown, FaSortNumericDown, FaSortAmountDown } from 'react-icons/fa';
import ItemDisplayer from './components/ItemDisplayer/ItemDisplayer';
import CreateSubcategory from './components/CreateSubcategory/CreateSubcategory';
import AddSubCategory from './components/AddSubcategory/AddSubcategory';
import RemoveSubcategory from './components/RemoveSubcategory/RemoveSubcategory';
import CreateCategory from './components/CreateCategory/CreateCategory';
import EmptyCategory from './components/EmptyCategory/EmptyCategory';
import AddCategory from './components/AddCategory/AddCategory';
import RemoveCategory from './components/RemoveCategory/RemoveCategory';
import AddCategoryModal from './components/AddCategoryModal/AddCategoryModal';
import { X } from 'react-bootstrap-icons';
import './CategoryDisplayer.scss';
import { MenuCategoryNode, MenuType } from '../../../../types/menu.types';
import { moveCategoryPosition, setCategoryPosition, normalizeMenuCategoryPositions } from '../../../../../../../../crud/menu_api/categories';
import { toast } from 'react-toastify';

interface CategoryDisplayerProps {
  menus: MenuType[];
  setMenus: (data: any) => void;
  selectedMenu: MenuType | null;
  setSelectedMenu: (menu: MenuType | null) => void;
  selectedParentCategory: MenuCategoryNode | null;
  setSelectedParentCategory: (category: MenuCategoryNode | null) => void;
  selectedSubcategory: MenuCategoryNode | null;
  setSelectedSubcategory: (category: MenuCategoryNode | null) => void;
  initialCategoryId?: string;
  initialSubcategoryId?: string;
  refreshMenus?: () => void;
}

const CategoryDisplayer: React.FC<CategoryDisplayerProps> = ({
  menus,
  setMenus,
  selectedMenu,
  setSelectedMenu,
  selectedParentCategory,
  setSelectedParentCategory,
  selectedSubcategory,
  setSelectedSubcategory,
  initialCategoryId,
  initialSubcategoryId,
  refreshMenus,
}) => {
  const [showRemoveCategoryFromMenu, setShowRemoveCategoryFromMenu] = useState(false);
  const [showRemoveSubcategoryFromMenu, setShowRemoveSubcategoryFromMenu] = useState(false);
  const [menuCategories, setMenuCategories] = useState<MenuCategoryNode[]>(selectedMenu?.categories || []);
  const [selectedCategory, setSelectedCategory] = useState<MenuCategoryNode | null>(null);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddSubcategoryModal, setShowAddSubcategoryModal] = useState(false);
  const [isAddCategoryVisible, setIsAddCategoryVisible] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateSubcategoryModal, setShowCreateSubcategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [showEmptyCategoryModal, setShowEmptyCategoryModal] = useState(false);
  const [selectedEmptyCategory, setSelectedEmptyCategory] = useState<MenuCategoryNode | null>(null);
  const [showButtonCreateSubCategory, setShowButtonCreateSubCategory] = useState(false);
  const [indice, setIndice] = useState(0);
  const [isMovingCategory, setIsMovingCategory] = useState<number | null>(null);
  const [isNormalizingPositions, setIsNormalizingPositions] = useState(false);
  const navigate = useNavigate();

  /**
   * Sort categories by position. Categories with a position come first (sorted ascending),
   * followed by categories with null position (maintain original order).
   */
  const sortCategoriesByPosition = (categories: MenuCategoryNode[]): MenuCategoryNode[] => {
    return [...categories].sort((a, b) => {
      // Both have position: sort by position
      if (a.position !== null && a.position !== undefined && b.position !== null && b.position !== undefined) {
        return a.position - b.position;
      }
      // Only a has position: a comes first
      if (a.position !== null && a.position !== undefined) return -1;
      // Only b has position: b comes first
      if (b.position !== null && b.position !== undefined) return 1;
      // Both null: maintain original order
      return 0;
    });
  };

  /**
   * Update a category's position in the local state (both menuCategories and parent menus)
   * Returns a rollback function to undo the changes if needed
   */
  const updateCategoryPositionInState = (
    categoryId: number, 
    newPosition: number, 
    swappedCategoryId?: number, 
    swappedPosition?: number
  ): (() => void) => {
    // Store current state for rollback
    const prevMenuCategories = [...menuCategories];
    const prevMenus = [...menus];

    // Helper to update categories array
    const updateCategories = (categories: MenuCategoryNode[]): MenuCategoryNode[] => {
      return categories.map(cat => {
        if (cat.id === categoryId) {
          return { ...cat, position: newPosition };
        }
        if (swappedCategoryId && cat.id === swappedCategoryId && swappedPosition !== undefined) {
          return { ...cat, position: swappedPosition };
        }
        return cat;
      });
    };

    // Update local menuCategories state
    setMenuCategories(prev => updateCategories(prev));

    // Update the parent menus state to keep everything in sync
    if (selectedMenu) {
      const updatedMenus = menus.map(menu => {
        if (menu.menu_id === selectedMenu.menu_id) {
          // If we're at root level, update menu.categories
          if (!selectedParentCategory) {
            return { ...menu, categories: updateCategories(menu.categories) };
          } else {
            // If we're inside a parent category, update subcategories
            return {
              ...menu,
              categories: menu.categories.map(cat => {
                if (cat.id === selectedParentCategory.id) {
                  return { ...cat, subcategories: updateCategories(cat.subcategories) };
                }
                return cat;
              })
            };
          }
        }
        return menu;
      });
      setMenus(updatedMenus);
    }

    // Return rollback function
    return () => {
      setMenuCategories(prevMenuCategories);
      setMenus(prevMenus);
    };
  };

  /**
   * Handle moving a category up or down (optimistic update)
   */
  const handleMoveCategory = async (e: React.MouseEvent, category: MenuCategoryNode, moveUp: boolean) => {
    e.stopPropagation();
    if (!selectedMenu || isMovingCategory) return;
    
    // Find the category to swap with
    const currentCategories = selectedParentCategory ? selectedParentCategory.subcategories : menuCategories;
    const sortedCategories = sortCategoriesByPosition(currentCategories);
    const currentIndex = sortedCategories.findIndex(c => c.id === category.id);
    
    // Determine swap target
    const swapIndex = moveUp ? currentIndex - 1 : currentIndex + 1;
    if (swapIndex < 0 || swapIndex >= sortedCategories.length) return;
    
    const swapCategory = sortedCategories[swapIndex];
    const oldPosition = category.position;
    const swapPosition = swapCategory.position;
    
    // Optimistic update: swap positions immediately
    const rollback = updateCategoryPositionInState(
      category.id,
      swapPosition ?? oldPosition ?? 1,
      swapCategory.id,
      oldPosition ?? swapPosition ?? 2
    );
    
    // Make API call in background
    try {
      await moveCategoryPosition(selectedMenu.menu_id, category.id, moveUp);
      // Success - no need to do anything, UI is already updated
    } catch (error: any) {
      // Rollback on error
      rollback();
      const errorMessage = error?.response?.data?.message || 'Error al mover la categoría';
      toast.error(errorMessage);
    }
  };

  /**
   * Initialize position for a category that has null position
   */
  const handleInitializePosition = async (e: React.MouseEvent, category: MenuCategoryNode) => {
    e.stopPropagation();
    if (!selectedMenu || isMovingCategory) return;
    
    // Find the max position among siblings and set this category to max + 1
    const categories = selectedParentCategory ? selectedParentCategory.subcategories : selectedMenu.categories;
    const maxPosition = categories.reduce((max, cat) => {
      if (cat.position !== null && cat.position !== undefined && cat.position > max) {
        return cat.position;
      }
      return max;
    }, 0);
    
    const newPosition = maxPosition + 1;
    
    // Optimistic update
    const rollback = updateCategoryPositionInState(category.id, newPosition);
    
    try {
      await setCategoryPosition(selectedMenu.menu_id, category.id, newPosition);
      toast.success('Posición asignada exitosamente');
    } catch (error: any) {
      // Rollback on error
      rollback();
      const errorMessage = error?.response?.data?.message || 'Error al asignar posición';
      toast.error(errorMessage);
    }
  };

  /**
   * Check if a category can move up (not first positioned item)
   */
  const canMoveUp = (category: MenuCategoryNode, sortedCategories: MenuCategoryNode[]): boolean => {
    if (category.position === null || category.position === undefined) return false;
    const index = sortedCategories.findIndex(c => c.id === category.id);
    return index > 0;
  };

  /**
   * Check if a category can move down (not last positioned item)
   */
  const canMoveDown = (category: MenuCategoryNode, sortedCategories: MenuCategoryNode[]): boolean => {
    if (category.position === null || category.position === undefined) return false;
    const index = sortedCategories.findIndex(c => c.id === category.id);
    // Can move down if not last in the list
    return index < sortedCategories.length - 1;
  };

  /**
   * Normalize category positions in the menu.
   * This transforms positions like [2, 5, 7] into [1, 2, 3] to avoid gaps.
   * 
   * IMPORTANT: Position normalization respects the hierarchical structure:
   * - If parentCategoryId is provided: Only normalizes subcategories of that parent
   * - If parentCategoryId is null/undefined: Normalizes all levels (root + all subcategory groups)
   * 
   * Example: Parent categories P1, P2, P3 with positions [3,4,5] become [1,2,3]
   * And P3's children C1, C2, C3 with positions [2,5,7] become [1,2,3] (independent level)
   * 
   * @param parentCategoryId - Optional. If provided, only normalizes children of this parent.
   */
  const handleNormalizePositions = async (parentCategoryId?: number | null) => {
    if (!selectedMenu || isNormalizingPositions) return;
    
    setIsNormalizingPositions(true);
    try {
      // Pass parentCategoryId to normalize only that level, or undefined to normalize all
      const response: any = await normalizeMenuCategoryPositions(selectedMenu.menu_id, parentCategoryId);
      const { changes_made, levels } = response.data;
      
      if (changes_made > 0) {
        // Update local state with normalized positions from the response
        // The response contains all levels with their new positions
        const updateCategoriesWithNormalizedPositions = (
          categories: MenuCategoryNode[],
          positionsMap: Map<number, number>
        ): MenuCategoryNode[] => {
          return categories.map(cat => {
            const newPosition = positionsMap.get(cat.id);
            const updatedCat = newPosition !== undefined 
              ? { ...cat, position: newPosition }
              : cat;
            
            // Also update subcategories if they exist
            if (updatedCat.subcategories && updatedCat.subcategories.length > 0) {
              return {
                ...updatedCat,
                subcategories: updateCategoriesWithNormalizedPositions(updatedCat.subcategories, positionsMap)
              };
            }
            return updatedCat;
          });
        };
        
        // Build a map of category_id -> new position from all levels
        const positionsMap = new Map<number, number>();
        levels.forEach((level: any) => {
          level.positions.forEach((pos: { category_id: number; position: number }) => {
            positionsMap.set(pos.category_id, pos.position);
          });
        });
        
        // Update menuCategories state
        setMenuCategories(prev => updateCategoriesWithNormalizedPositions(prev, positionsMap));
        
        // Update menus state to keep everything in sync
        const updatedMenus = menus.map(menu => {
          if (menu.menu_id === selectedMenu.menu_id) {
            return {
              ...menu,
              categories: updateCategoriesWithNormalizedPositions(menu.categories, positionsMap)
            };
          }
          return menu;
        });
        setMenus(updatedMenus);
        
        // Also update selectedParentCategory if we're normalizing its subcategories
        if (parentCategoryId && selectedParentCategory && selectedParentCategory.id === parentCategoryId) {
          const updatedSubcategories = updateCategoriesWithNormalizedPositions(
            selectedParentCategory.subcategories, 
            positionsMap
          );
          setSelectedParentCategory({
            ...selectedParentCategory,
            subcategories: updatedSubcategories
          });
        }
        
        const levelLabel = parentCategoryId ? 'subcategorías' : 'categorías';
        toast.success(`Posiciones de ${levelLabel} normalizadas: ${changes_made} cambio(s)`);
      } else {
        toast.info('Las posiciones ya están normalizadas');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Error al normalizar posiciones';
      toast.error(errorMessage);
    } finally {
      setIsNormalizingPositions(false);
    }
  };

  useEffect(() => {
    if (selectedMenu && initialCategoryId) {
      const cat = selectedMenu.categories.find((c) => String(c.id) === initialCategoryId);
      if (cat) {
        if (cat.subcategories && cat.subcategories.length > 0) {
          // Parent category - show subcategories
          navigate(`/App/Menus/${selectedMenu.menu_id}/${cat.id}`);
          setSelectedParentCategory(cat);
          setMenuCategories(cat.subcategories);
          setIndice(1);
        } else if (cat.items && cat.items.length > 0) {
          // Terminal category - show items directly
          navigate(`/App/Menus/${selectedMenu.menu_id}/${cat.id}`);
          setSelectedCategory(cat);
          setMenuCategories([]);
          setIndice(1);
        } else {
          // Empty category - show EmptyCategory modal without navigation
          setSelectedEmptyCategory(cat);
          setShowEmptyCategoryModal(true);
          // Don't navigate, don't set parent category, keep current state
        }
      }
    }
  }, [selectedMenu, initialCategoryId, setSelectedParentCategory, navigate]);

  useEffect(() => {
    if (selectedMenu && selectedMenu.categories && !initialCategoryId) {
      // Only set to all categories if we're not navigating to a specific category
      setMenuCategories(selectedMenu.categories);
    }
  }, [selectedMenu, initialCategoryId]);
  
  // Additional effect to sync menuCategories when selectedMenu updates and we're at the root level
  useEffect(() => {
    if (selectedMenu && selectedMenu.categories && !selectedParentCategory && !selectedCategory) {
      setMenuCategories(selectedMenu.categories);
    }
  }, [selectedMenu, selectedParentCategory, selectedCategory]);
  
  
  useEffect(() => {
    if (selectedParentCategory && initialSubcategoryId) {
      const sub = selectedParentCategory.subcategories.find((s: MenuCategoryNode) => String(s.id) === initialSubcategoryId);
      if (sub) {
        setSelectedSubcategory(sub);
        setSelectedCategory(sub);
      }
    }
  }, [selectedParentCategory, initialSubcategoryId, setSelectedSubcategory]);

  const handleRemoveCategoryFromMenuClick = (selectedCat: MenuCategoryNode) => {
    if (selectedParentCategory) {
      setSelectedSubcategory(selectedCat);
      setShowRemoveSubcategoryFromMenu(true);
    } else {
      setSelectedCategory(selectedCat);
      setShowRemoveCategoryFromMenu(true);
    }
  };

  function noOpenModals() {
    return !showAddCategoryModal && !showRemoveCategoryFromMenu;
  }
  const handleCategoryClick = (category: MenuCategoryNode) => {
    // Si la categoría está vacía, mostrar siempre el modal de EmptyCategory
    if (
      (!category.items || category.items.length === 0) &&
      (!category.subcategories || category.subcategories.length === 0) &&
      indice === 0
    ) {
      setSelectedEmptyCategory(category);
      setShowEmptyCategoryModal(true);
      setShowButtonCreateSubCategory(false);
      return;
    }
    
    // Handle categories with items
    if (category.items && category.items.length > 0) {
      setSelectedCategory(category);
      setShowButtonCreateSubCategory(true);
      if (!(selectedParentCategory || selectedSubcategory)) {
        navigate(`/App/Menus/${selectedMenu?.menu_id}/${category.id}`);
        setIndice((prev) => {
          const nuevo = prev + 1;
          return nuevo;
        });
      }
    }
    // Handle categories with subcategories
    else if (category.subcategories && category.subcategories.length > 0) {
      if (!selectedParentCategory) {
        // First level category with subcategories
        setSelectedParentCategory(category);
        setMenuCategories(category.subcategories);
        setSelectedCategory(null);
        setShowButtonCreateSubCategory(true);
        navigate(`/App/Menus/${selectedMenu?.menu_id}/${category.id}`);
        setIndice((prev) => {
          const nuevo = prev + 1;
          return nuevo;
        });
      } else {
        // Second level category (subcategory) with further subcategories
        setSelectedSubcategory(category);
        setMenuCategories(category.subcategories);
        setSelectedCategory(null);
        setShowButtonCreateSubCategory(true);
        navigate(`/App/Menus/${selectedMenu?.menu_id}/${selectedParentCategory.id}/${category.id}`);
      }
    }
    // Handle empty categories that are already selected as parent/sub
    else {
      if (selectedParentCategory || selectedSubcategory) {
        setSelectedCategory(category);
        setShowButtonCreateSubCategory(true);
      }
    }
  };

  const handleGoBack = () => {
     setShowButtonCreateSubCategory(false);

    // 1) Si estamos en la vista de items de una categoría
    if (selectedCategory) {
      // volvemos al listing de subcategorías
      navigate(`/App/Menus/${selectedMenu?.menu_id}/${selectedParentCategory?.id}`);
      setSelectedCategory(null);
      return;
    }

    // 2) Si estamos dentro de una subcategoría
    if (selectedSubcategory) {
      navigate(`/App/Menus/${selectedMenu?.menu_id}/${selectedParentCategory?.id}`);
      setSelectedSubcategory(null);
      setIndice(prev => {
        const nuevo = prev - 1;
        return nuevo;
      });
      return;
    }    // 3) Si estamos dentro de una categoría de primer nivel
    if (selectedParentCategory) {
      navigate('/App/Menus');
      setSelectedParentCategory(null);
      setSelectedMenu(null);
      setIndice(prev => {
        const nuevo = prev - 1;
        return nuevo;
      });
      // restauramos el listing raíz de categorías
      setMenuCategories(selectedMenu!.categories);
      return;
    }

    // 4) Finalmente, si aún hay menu seleccionado, volvemos al listado de menús
    if (selectedMenu) {
      navigate('/App/Menus');
      setSelectedMenu(null);
      return;
    }
  };

  const updateCategoryItems = (updatedCategory: MenuCategoryNode) => {
    setMenuCategories((prevCategories) => {
      return prevCategories.map((cat) => {
        if (cat.id === updatedCategory.id) {
          return { ...cat, items: updatedCategory.items };
        }
        return cat;
      });
    });
  }



  const renderCategoryGrid = (categories: MenuCategoryNode[]) => {
    const validCategories = Array.isArray(categories) ? categories : [];
    const sortedCategories = sortCategoriesByPosition(validCategories);
    
    return (
      <div className="categories-grid">
        {sortedCategories.length === 0 ? (
          <div className="no-categories">
            <p>No hay categorías disponibles.</p>
            <p>Crea una nueva categoría para comenzar a organizar tu menú.</p>
          </div>
        ) : (
          sortedCategories.map((category, index) => {
            const hasSubcategories = category.subcategories && category.subcategories.length > 0;
            const hasItems = category.items && category.items.length > 0;
            const isEmpty = !hasSubcategories && !hasItems;
            const hasPosition = category.position !== null && category.position !== undefined;
            const isBeingMoved = isMovingCategory === category.id;
            
            return (
              <div
                key={category.id}
                className={`category-card ${
                  hasSubcategories ? 'has-subcategories' : ''
                } ${hasItems ? 'has-items' : ''} ${isEmpty ? 'empty' : ''} ${isBeingMoved ? 'moving' : ''}`}
                onClick={() => handleCategoryClick(category)}
              >
                <h3 className="category-title">
                  <div className="category-name-content">
                    {category.subcategories && category.subcategories.length > 0 ? (
                      <FaFolder className="category-icon folder-icon" />
                    ) : (
                      <FaList className="category-icon list-icon" />
                    )}
                    {category.name}
                  </div>
                  <div className="category-actions">
                    {/* Position indicator and controls */}
                    <div className="position-controls">
                      {hasPosition ? (
                        <>
                          <span className="position-badge" title={`Posición: ${category.position}`}>
                            #{category.position}
                          </span>
                          <button
                            className={`action-button move-button ${!canMoveUp(category, sortedCategories) ? 'disabled' : ''}`}
                            onClick={(e) => handleMoveCategory(e, category, true)}
                            disabled={!canMoveUp(category, sortedCategories) || isBeingMoved}
                            title="Mover arriba"
                          >
                            <FaChevronUp />
                          </button>
                          <button
                            className={`action-button move-button ${!canMoveDown(category, sortedCategories) ? 'disabled' : ''}`}
                            onClick={(e) => handleMoveCategory(e, category, false)}
                            disabled={!canMoveDown(category, sortedCategories) || isBeingMoved}
                            title="Mover abajo"
                          >
                            <FaChevronDown />
                          </button>
                        </>
                      ) : (
                        <button
                          className="action-button init-position-button"
                          onClick={(e) => handleInitializePosition(e, category)}
                          disabled={isBeingMoved}
                          title="Asignar posición"
                        >
                          <FaSortNumericDown />
                        </button>
                      )}
                    </div>
                    <button
                      className="action-button remove-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveCategoryFromMenuClick(category);
                      }}
                      title="Quitar categoría del menú"
                    >
                      <FaUnlink />
                    </button>
                  </div>
                </h3>
                
                <div className="category-content">
                  {hasSubcategories && (
                    <div className="subcategories-container">
                      {category.subcategories.slice(0, 8).map((sub, idx) => (
                        <div key={idx} className="subcategory-box" title={sub.name}>
                          {sub.name}
                        </div>
                      ))}
                      {category.subcategories.length > 8 && (
                        <div className="more-indicator">
                          +{category.subcategories.length - 8} más
                        </div>
                      )}
                    </div>
                  )}
                  
                  {hasItems && !hasSubcategories && (
                    <div className="items-container">
                      {category.items.slice(0, 6).map((item, idx) => (
                        <div key={idx} className="item-box" title={item.item_name}>
                          {item.item_name}
                        </div>
                      ))}
                      {category.items.length > 6 && (
                        <div className="more-indicator">
                          +{category.items.length - 6} más
                        </div>
                      )}
                    </div>
                  )}
                  
                  {isEmpty && (
                    <div className="empty-category-indicator">
                      <p>Categoría vacía</p>
                      <small>Haz clic para agregar subcategorías o elementos</small>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  return (
    <>
      <div className="category-display-actions">
        {/* Un solo botón Volver, ahora con lógica de índice */}
        {!selectedCategory && (
          <Button variant="none" className="mp-button white" onClick={handleGoBack}>
            <FaChevronLeft /> Volver
          </Button>
        )}
        {selectedMenu && !selectedCategory && !selectedParentCategory && (
          <>
            <Button variant="none" className="mp-button white" onClick={() => setShowAddCategoryModal(true)}>
              <FaPlus /> Agregar categoría
            </Button>
            <Button 
              variant="none" 
              className="mp-button white normalize-button" 
              onClick={() => handleNormalizePositions()}
              disabled={isNormalizingPositions}
              title="Normalizar posiciones de categorías (ej: [2,5,7] → [1,2,3])"
            >
              <FaSortAmountDown /> {isNormalizingPositions ? 'Ordenando...' : 'Ordenar Categorías'}
            </Button>
          </>
        )}
        {(showButtonCreateSubCategory && selectedParentCategory?.subcategories?.length! > 0 && !selectedCategory && indice === 1) && (
          <>
            <Button variant="none" className="mp-button white" onClick={() => setShowSubcategoryModal(true)}>
              <FaFolder /> Agregar subcategoría
            </Button>
            <Button 
              variant="none" 
              className="mp-button white normalize-button" 
              onClick={() => selectedParentCategory && handleNormalizePositions(selectedParentCategory.id)}
              disabled={isNormalizingPositions}
              title="Normalizar posiciones de subcategorías (ej: [2,5,7] → [1,2,3])"
            >
              <FaSortAmountDown /> {isNormalizingPositions ? 'Ordenando...' : 'Ordenar Subcategorías'}
            </Button>
          </>
        )}
      </div>
      {(!selectedCategory || !noOpenModals()) && menuCategories.length > 0 && renderCategoryGrid(menuCategories)}
      {!selectedCategory && menuCategories.length === 0 && selectedParentCategory && (
        <div className="empty-category-indicator">
          <h3>Category: {selectedParentCategory.name}</h3>
          <p>Esta categoría está actualmente vacía. Usa el botón "Agregar Item" para añadir elementos a esta categoría.</p>
        </div>
      )}
      {selectedCategory && noOpenModals() && (
        <ItemDisplayer
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          updateCategoryItems={updateCategoryItems}
        />
      )}
      {showRemoveCategoryFromMenu && (
        <RemoveCategory
          show={showRemoveCategoryFromMenu}
          handleClose={() => setShowRemoveCategoryFromMenu(false)}
          menu={selectedMenu}
          category={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          menus={menus}
          setMenus={setMenus}
          setSelectedMenu={setSelectedMenu}
        />
      )}
      {showRemoveSubcategoryFromMenu && selectedSubcategory && (
        <RemoveSubcategory
          show={showRemoveSubcategoryFromMenu}
          handleClose={() => setShowRemoveSubcategoryFromMenu(false)}
          subcategory={selectedSubcategory}
        />
      )}
      
      <AddCategoryModal
        show={showAddCategoryModal}
        onHide={() => setShowAddCategoryModal(false)}
        onAddExisting={() => setIsAddCategoryVisible(true)}
        onCreateNew={() => setShowCreateModal(true)}
      />

      <Modal show={showSubcategoryModal} onHide={() => setShowSubcategoryModal(false)} centered className="add-category-modal">
        <Modal.Header>
          <div className="form-modal-title-container">
            <Modal.Title className="form-modal-title">Agregar Subcategoría</Modal.Title>
          </div>
          <Button variant="none" className="modal-close-button" onClick={() => setShowSubcategoryModal(false)}>
            <X size={24} />
          </Button>
        </Modal.Header>
        <Modal.Body>
          <div className="category-options-container">
            <div
              className="category-option"
              onClick={() => {
                setShowAddSubcategoryModal(true);
                setShowSubcategoryModal(false);
              }}
            >
              <span className="option-title">Agregar existente</span>
              <p className="option-description">
                Puedes elegir entre subcategorías que ya tengas creadas
              </p>
            </div>
            <div
              className="category-option"
              onClick={() => {
                setShowCreateSubcategoryModal(true);
                setShowSubcategoryModal(false);
              }}
            >
              <span className="option-title">Crear subcategoría</span>
              <p className="option-description">
                Una subcategoría desde cero
              </p>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {isAddCategoryVisible && (
        <AddCategory
          show={isAddCategoryVisible}
          handleClose={() => setIsAddCategoryVisible(false)}
          menu={selectedMenu}
          setSelectedMenu={setSelectedMenu}
          menus={menus}
          setMenus={setMenus}
        />
      )}
      <AddSubCategory
        show={showAddSubcategoryModal}
        handleClose={() => {
          setShowAddSubcategoryModal(false);
          // Si la categoría sigue vacía, mostrar nuevamente el modal de EmptyCategory
          if (selectedParentCategory && (!selectedParentCategory.items || selectedParentCategory.items.length === 0) && (!selectedParentCategory.subcategories || selectedParentCategory.subcategories.length === 0)) {
            setSelectedEmptyCategory(selectedParentCategory);
            setShowEmptyCategoryModal(true);
          }
        }}
        category={selectedParentCategory}
        setSelectedCategory={setSelectedParentCategory}
        categories={menus}
        setCategories={setMenus}
      />
      <CreateSubcategory
        showCreateModal={showCreateSubcategoryModal}
        setShowCreateModal={() => setShowCreateSubcategoryModal(false)}
        menu={selectedMenu}
        categoryId={selectedParentCategory}
      />
      <CreateCategory
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        menu={selectedMenu}
        setSelectedMenu={setSelectedMenu}  // Pasar este prop
        setMenus={setMenus} 
        menus={menus}
      />
      {/* NEW: Render the Empty Category modal */}
      {showEmptyCategoryModal && selectedEmptyCategory && (
        <EmptyCategory
          show={showEmptyCategoryModal}
          handleClose={() => setShowEmptyCategoryModal(false)}
          category={selectedEmptyCategory}
          onSelectAddItems={() => {
            setSelectedCategory(selectedEmptyCategory);
            navigate(`/App/Menus/${selectedMenu?.menu_id}/${selectedEmptyCategory.id}`, {
              state: { 
                openCreateItemModal: true,
                selectedCategoryId: selectedEmptyCategory?.id,
                selectedCategoryName: selectedEmptyCategory?.name
              }
            });
            setShowEmptyCategoryModal(false);
          }}
          onSelectAddSubcategory={() => {
            setSelectedParentCategory(selectedEmptyCategory);
            setShowAddSubcategoryModal(true);
            setShowEmptyCategoryModal(false);
          }}
        />
      )}
    </>
  );
};

export default CategoryDisplayer;
